import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import CustomDateRangePicker, {
  dateFilter,
  getDateFilterOption,
} from 'general/components/Others/CustomDateRangePicker';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import AppColors from 'general/constants/AppColors';
import AppConfigs from 'general/constants/AppConfigs';
import AppRoute from 'general/constants/AppRoute';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import * as ExcelJS from 'exceljs';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

ChargeTransactionCompactListView.propTypes = {
  prefKey: PropTypes.string,
  title: PropTypes.string,
  initialFilter: PropTypes.object,
  chargePoint: PropTypes.object,
};

const EXPORT_PAGE_SIZE = 500;

const sanitizeExcelText = (value) => {
  const text = value == null ? '' : String(value);
  return /^[=+\-@]/.test(text) ? `'${text}` : text;
};

const normalizeContactValue = (value) => String(value ?? '').trim().toLowerCase();

function ChargeTransactionCompactListView({ prefKey, title, initialFilter, chargePoint }) {
  // MARK: ---- PROPERTIES ----
  const omitColumnIds = useMemo(() => {
    if (Utils.isValid(initialFilter?.vehicleId)) {
      return ['PLATE', 'DRIVER', 'VEHICLE_TYPE', 'VEHICLE_MODEL', 'PHONE'];
    }
    if (Utils.isValid(initialFilter?.pointId)) {
      return ['STATION'];
    }
    return [];
  }, [initialFilter]);
  const defaultColumnIds = useMemo(() => {
    return [
      'TRANSACTION_CODE',
      'START_TIME',
      'END_TIME',
      'STATUS',
      'CONNECTOR',
      'PLATE',
      'DRIVER',
      'PHONE',
      'VEHICLE_TYPE',
      'VEHICLE_MODEL',
      'ENERGY',
      'AMOUNT',
      'STATION',
    ].filter((id) => !omitColumnIds.includes(id));
  }, [omitColumnIds]);
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [exportingCustomers, setExportingCustomers] = useState(false);
  const [chargeTransactionList, setChargeTransactionList] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({
    page: 0,
    limit: AppConfigs.defaultPageLimit,
    q: '',
    status: '',
    from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
    to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
    dateFilter: dateFilter.baMuoiNgayGanNhat,
    ...initialFilter,
  });
  const fullColumns = useMemo(() => {
    return [
      {
        id: 'TRANSACTION_CODE',
        name: UIHelper.buildTableColumn('Mã phiên sạc'),
        selector: (row) => row?.transactionCode,
        minWidth: '110px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'START_TIME',
        name: UIHelper.buildTableColumn('Thời gian bắt đầu'),
        selector: (row) =>
          row?.startTime ? Utils.formatDateTimeToString(row?.startTime, 'DD/MM/YYYY - HH:mm') : '',
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'END_TIME',
        name: UIHelper.buildTableColumn('Thời gian kết thúc'),
        selector: (row) =>
          row?.endTime ? Utils.formatDateTimeToString(row?.endTime, 'DD/MM/YYYY - HH:mm') : '',
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATUS',
        name: UIHelper.buildTableColumn('Trạng thái'),
        selector: (row) => {
          return (
            <KTLabel
              text={ChargeHelper.Utils.getChargeTransactionStatusName(row?.status)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${ChargeHelper.Utils.getChargeTransactionStatusColor(
                row?.status
              )}`}
            />
          );
        },
        minWidth: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'CONNECTOR',
        name: UIHelper.buildTableColumn('Vòi sạc'),
        selector: (row) => !initialFilter?.pointId
          ? row?.chargeConnector?.connectorCode
          : `Vòi sạc ${row?.chargeConnector?.connectorNumber}`,
        minWidth: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'PLATE',
        name: UIHelper.buildTableColumn('Biển số xe'),
        selector: (row) => (
          <span
            className={`${Utils.isValid(row?.vehicleId) && _.toNumber(row?.vehicleId) !== 0
              ? 'cursor-pointer hover-opacity-60'
              : ''
              }`}
            onClick={() => {
              if (Utils.isValid(row?.vehicleId) && _.toNumber(row?.vehicleId) !== 0) {
                Utils.openInNewTab(`${AppRoute.vehicleList}/${row?.vehicleId}`);
              }
            }}
          >
            {Utils.isValid(row?.vehicleId) && _.toNumber(row?.vehicleId) !== 0 ? (
              row?.plate
            ) : (
              <span style={{ color: '#72728B' }}>{row?.plate}</span>
            )}
          </span>
        ),
        minWidth: '140px',
        style: tableStyle.columns.link,
      },
      {
        id: 'DRIVER',
        name: UIHelper.buildTableColumn('Tên chủ xe'),
        selector: (row) => (
          <span
            className={`${Utils.isValid(row?.user?.userId) && _.toNumber(row?.user?.userId) !== 0
              ? 'cursor-pointer hover-opacity-60'
              : ''
              }`}
            onClick={() => {
              if (Utils.isValid(row?.user?.userId) && _.toNumber(row?.user?.userId) !== 0) {
                Utils.openInNewTab(`${AppRoute.customerDetail}/${row?.user?.userId}`);
              }
            }}
          >
            {Utils.isValid(row?.user?.userId) && _.toNumber(row?.user?.userId) !== 0 ? (
              row?.user?.fullname
            ) : (
              <span style={{ color: '#72728B' }}>Vãng lai</span>
            )}
          </span>
        ),
        minWidth: '180px',
        style: tableStyle.columns.link,
      },
      {
        id: 'PHONE',
        name: UIHelper.buildTableColumn('Số điện thoại'),
        selector: (row) => row?.user?.phone,
        minWidth: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'VEHICLE_TYPE',
        name: UIHelper.buildTableColumn('Loại phương tiện'),
        selector: (row) =>
          row?.vehicle?.vehicleType
            ? _.find(
              ChargeHelper.Data.vehicleTypeOptions,
              (item) => item.value === row?.vehicle?.vehicleType
            )?.name
            : '',
        minWidth: '130px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'VEHICLE_MODEL',
        name: UIHelper.buildTableColumn('Dòng xe'),
        selector: (row) => <span className="">{row?.vehicle?.model?.name}</span>,
        width: '130px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'ENERGY',
        name: UIHelper.buildTableColumn('Lượng điện đã sạc', 'kWh'),
        selector: (row) =>
          Utils.isValid(row?.energy) ? Utils.formatNumber((_.toNumber(row?.energy) / 1000).toFixed(3)) : '',
        width: '120px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'AMOUNT',
        name: UIHelper.buildTableColumn('Số tiền sạc', 'VNĐ'),
        selector: (row) =>
          row?.status === ChargeConstants.ChargeTransactionStatus.FINISHED
            ? Utils.formatNumber(
              row?.totalCost
            )
            : Utils.formatNumber(row?.estimatedEnergyCost),
        width: '110px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATION',
        name: UIHelper.buildTableColumn('Trạm sạc'),
        selector: (row) => (
          <span
            className="cursor-pointer hover-opacity-60"
            onClick={() => {
              Utils.openInNewTab(
                `${AppRoute.stationList}/${row?.chargeConnector?.chargePoint?.stationId}`
              );
            }}
          >
            {row?.chargeConnector?.chargePoint?.chargeStation?.name}
          </span>
        ),
        minWidth: '180px',
        style: tableStyle.columns.link,
      },
      {
        name: UIHelper.buildTableColumn('Thao tác'),
        width: '80px',
        center: true,
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        cell: (row) => {
          return (
            <div className="d-flex gap-2 align-items-center justify-content-center">
              <KTTooltip offset={[0, 0]} text="Xem chi tiết">
                <a
                  className="btn btn-sm btn-clean btn-icon"
                  onClick={() => {
                    router.navigate(`transaction/${row?.transactionId}`);
                  }}
                >
                  <i
                    className="fa-duotone fa-eye"
                    style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                  />
                </a>
              </KTTooltip>
            </div>
          );
        },
      },
    ];
  }, [initialFilter]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns.filter(column => !column.omit).map(column => column.id).filter(Boolean) || []
  );
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);
  useEffect(() => {
    const params = _.pickBy(
      filter,
      (value) => value !== null && value !== undefined && value !== ''
    );
    ChargeHelper.API.ChargeTransaction.requestGetChargeTransactionList(params).then((data) => {
      setChargeTransactionList(data?.chargeTransactions);
      setTotal(data?.total);
    });
  }, [filter]);
  const [selectedChargeStation, setSelectedChargeStation] = useState(null);
  const dropdownStationEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="stationId"
        name="stationId"
        toggleElement={
          <button
            className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
            type="button"
          >
            {selectedChargeStation?.name ?? 'Chọn trạm sạc'}
          </button>
        }
        selectedValue={selectedChargeStation}
        dropdownMenuItems={[{ name: 'Trạm sạc', role: 'STATION' }]}
        getSubItems={async (item) => {
          return ChargeHelper.API.requestGetChargeStationList({
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
          }).then((data) => {
            return {
              values: data.chargeStations?.map((station) => ({
                id: station?.stationId,
                name: station?.name,
                item: station,
              })),
              total: data.total,
            };
          });
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={(value) => {
          setSelectedChargeStation(value);
          setFilter({
            ...filter,
            stationId: value?.id,
            page: 0,
          });
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        searchFormClassName="mx-3 my-1 search-form-border"
        ignoreConfirmClick={true}
        defaultValue={{
          name: 'Trạm sạc',
          role: 'STATION',
        }}
        footerEl={
          <div
            className="p-4 position-sticky sticky-bottom bg-white"
            style={{
              borderTop: `1px solid ${AppColors.gray[300]}`,
            }}
          >
            {UIHelper.buildNegativeButton('Bỏ chọn', () => {
              setSelectedChargeStation(null);
              setFilter({
                ...filter,
                stationId: '',
                page: 0,
              });
            })}
          </div>
        }
      />
    );
  }, [selectedChargeStation]);
  // useEffect(() => {
  //   const stationId = ChargeHelper.Filter.chargeTransactionList?.stationId;
  //   if (stationId) {
  //     ChargeHelper.API.getChargeStationDetail(stationId).then((data) => {
  //       setSelectedChargeStation({
  //         id: data?.stationId,
  //         name: data?.name,
  //         item: data,
  //       });
  //     });
  //   }
  // }, []);

  // MARK: ---- METHODS ----
  const getAllFilteredTransactions = async () => {
    const exportFilter = _.pickBy(
      _.omit(filter, ['page', 'limit', 'dateFilter']),
      (value) => value !== null && value !== undefined && value !== ''
    );
    const firstPage = await ChargeHelper.API.ChargeTransaction.requestGetChargeTransactionList({
      ...exportFilter,
      page: 0,
      limit: EXPORT_PAGE_SIZE,
    });
    const expectedTotal = Number(firstPage?.total) || 0;
    const transactions = [...(firstPage?.chargeTransactions ?? [])];

    if (expectedTotal > 0 && transactions.length === 0) {
      throw new Error('Không thể tải dữ liệu phiên sạc');
    }

    const pageCount = Math.ceil(expectedTotal / EXPORT_PAGE_SIZE);
    for (let page = 1; page < pageCount; page += 1) {
      const pageData = await ChargeHelper.API.ChargeTransaction.requestGetChargeTransactionList({
        ...exportFilter,
        page,
        limit: EXPORT_PAGE_SIZE,
      });
      const pageRows = pageData?.chargeTransactions ?? [];
      if (pageRows.length === 0) {
        throw new Error('Không thể tải đầy đủ dữ liệu phiên sạc');
      }
      transactions.push(...pageRows);
    }

    return transactions;
  };

  const buildUniqueCustomerList = (transactions) => {
    const customerMap = new Map();

    transactions.forEach((transaction) => {
      const user = transaction?.user ?? {};
      const phone = String(user?.phone ?? '').trim();
      const email = String(user?.email ?? '').trim();
      const plate = String(transaction?.plate ?? transaction?.vehicle?.plate ?? '').trim();
      const userId = user?.userId;
      const customerKey = Utils.isValid(userId) && Number(userId) !== 0
        ? `user:${userId}`
        : phone
          ? `phone:${normalizeContactValue(phone)}`
          : plate
            ? `plate:${normalizeContactValue(plate)}`
            : `transaction:${transaction?.transactionId}`;
      const current = customerMap.get(customerKey) ?? {
        customerCode: user?.userCode ?? '',
        fullname: user?.fullname || 'Khách vãng lai',
        phone,
        email,
        plates: new Set(),
        vehicleModels: new Set(),
        sessionCount: 0,
        totalEnergy: 0,
        lastChargeTime: null,
        lastStatus: '',
      };

      if (plate) current.plates.add(plate);
      const vehicleModel = transaction?.vehicle?.model?.name;
      if (vehicleModel) current.vehicleModels.add(vehicleModel);
      current.sessionCount += 1;
      current.totalEnergy += Number(transaction?.energy) || 0;

      const chargeTime = transaction?.startTime ? moment(transaction.startTime) : null;
      if (chargeTime?.isValid() && (!current.lastChargeTime || chargeTime.isAfter(current.lastChargeTime))) {
        current.lastChargeTime = chargeTime;
        current.lastStatus = ChargeHelper.Utils.getChargeTransactionStatusName(transaction?.status);
        current.fullname = user?.fullname || current.fullname;
        current.phone = phone || current.phone;
        current.email = email || current.email;
      }

      customerMap.set(customerKey, current);
    });

    return Array.from(customerMap.values()).sort((a, b) => {
      if (!a.lastChargeTime) return 1;
      if (!b.lastChargeTime) return -1;
      return b.lastChargeTime.valueOf() - a.lastChargeTime.valueOf();
    });
  };

  const exportCustomerList = async () => {
    if (exportingCustomers) return;

    setExportingCustomers(true);
    try {
      const transactions = await getAllFilteredTransactions();
      if (transactions.length === 0) {
        ToastHelper.showError('Không có khách hàng phù hợp với bộ lọc để xuất');
        return;
      }

      const customers = buildUniqueCustomerList(transactions);
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'EVG CMS';
      workbook.created = new Date();
      const sheet = workbook.addWorksheet('Danh sách khách hàng', {
        views: [{ state: 'frozen', ySplit: 4 }],
      });
      const pointName = chargePoint?.name || transactions[0]?.chargeConnector?.chargePoint?.name || '';
      const pointCode = chargePoint?.pointCode || transactions[0]?.chargeConnector?.chargePoint?.pointCode || initialFilter?.pointId;
      const stationName = chargePoint?.chargeStation?.name
        || transactions[0]?.chargeConnector?.chargePoint?.chargeStation?.name
        || transactions[0]?.chargeStation?.name
        || '';
      const pointLabel = [pointName, pointCode ? `(${pointCode})` : ''].filter(Boolean).join(' ');

      sheet.mergeCells('A1:K1');
      sheet.getCell('A1').value = 'DANH SÁCH KHÁCH HÀNG SẠC XE THEO TRỤ';
      sheet.getCell('A1').font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 14 };
      sheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };
      sheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF29618E' } };
      sheet.getRow(1).height = 28;

      sheet.mergeCells('A2:K2');
      sheet.getCell('A2').value = `Trụ sạc: ${pointLabel || initialFilter?.pointId}${stationName ? ` - Trạm: ${stationName}` : ''}`;
      sheet.getCell('A2').font = { bold: true, size: 11 };

      sheet.mergeCells('A3:K3');
      sheet.getCell('A3').value = `Khoảng thời gian: ${moment(filter?.from).format('DD/MM/YYYY')} - ${moment(filter?.to).format('DD/MM/YYYY')}${filter?.status ? ` | Trạng thái: ${ChargeHelper.Utils.getChargeTransactionStatusName(filter.status)}` : ''}`;
      sheet.getCell('A3').font = { italic: true, color: { argb: 'FF5E6278' } };

      sheet.columns = [
        { key: 'index', width: 7 },
        { key: 'customerCode', width: 18 },
        { key: 'fullname', width: 28 },
        { key: 'phone', width: 18 },
        { key: 'email', width: 30 },
        { key: 'plates', width: 22 },
        { key: 'vehicleModels', width: 24 },
        { key: 'sessionCount', width: 13 },
        { key: 'totalEnergy', width: 18 },
        { key: 'lastChargeTime', width: 22 },
        { key: 'lastStatus', width: 20 },
      ];

      const headerRow = sheet.getRow(4);
      headerRow.values = [
        'STT',
        'Mã khách hàng',
        'Tên khách hàng',
        'Số điện thoại',
        'Email',
        'Biển số xe',
        'Dòng xe',
        'Số phiên sạc',
        'Tổng điện đã sạc (kWh)',
        'Lần sạc gần nhất',
        'Trạng thái gần nhất',
      ];
      headerRow.height = 32;
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF28A745' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });

      customers.forEach((customer, index) => {
        const row = sheet.addRow({
          index: index + 1,
          customerCode: sanitizeExcelText(customer.customerCode),
          fullname: sanitizeExcelText(customer.fullname),
          phone: sanitizeExcelText(customer.phone),
          email: sanitizeExcelText(customer.email),
          plates: sanitizeExcelText(Array.from(customer.plates).join(', ')),
          vehicleModels: sanitizeExcelText(Array.from(customer.vehicleModels).join(', ')),
          sessionCount: customer.sessionCount,
          totalEnergy: customer.totalEnergy / 1000,
          lastChargeTime: customer.lastChargeTime?.format('DD/MM/YYYY - HH:mm') ?? '',
          lastStatus: customer.lastStatus,
        });
        row.getCell('D').numFmt = '@';
        row.getCell('I').numFmt = '#,##0.###';
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.alignment = { vertical: 'middle', wrapText: true };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFE4E6EF' } },
            left: { style: 'thin', color: { argb: 'FFE4E6EF' } },
            bottom: { style: 'thin', color: { argb: 'FFE4E6EF' } },
            right: { style: 'thin', color: { argb: 'FFE4E6EF' } },
          };
        });
      });

      sheet.autoFilter = { from: 'A4', to: `K${Math.max(4, customers.length + 4)}` };
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      const safePointCode = String(pointCode || initialFilter?.pointId || 'tru-sac').replace(/[^a-zA-Z0-9_-]/g, '_');
      anchor.href = url;
      anchor.download = `Danh_sach_KH_sac_${safePointCode}_${moment().format('YYYYMMDD')}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      ToastHelper.showSuccess(`Đã xuất ${customers.length} khách hàng từ ${transactions.length} phiên sạc`);
    } catch (error) {
      ToastHelper.showError(error?.message || 'Không thể xuất danh sách khách hàng');
    } finally {
      setExportingCustomers(false);
    }
  };

  return (
    <div>
      {UIHelper.buildCard(
        <div className="pb-4">
          {/* header */}
          <div className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between">
            <div className="d-flex flex-column">
              <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                {title || 'Danh sách phiên sạc'}
              </span>
              <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                Tổng:{' '}
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                  {Utils.formatNumber(total)} phiên sạc
                </span>
              </span>
            </div>
            {/* actions */}
            <div className="d-flex align-items-center">
              {Utils.isValid(initialFilter?.pointId) &&
                UIHelper.buildBlueButton(
                  exportingCustomers ? 'Đang xuất...' : 'Xuất DS khách hàng',
                  exportCustomerList,
                  <i className="fa-regular fa-file-excel" style={{ fontSize: 15 }} />,
                  false,
                  exportingCustomers
                )}
            </div>
          </div>

          {/* table */}
          <div className="">
            {/* filter */}
            <div className="row m-0 p-0 px-2">
              {/* tim kiem */}
              <div className="col-xl-3 col-lg-4 col-sm-6 mb-4 px-2">
                {UIHelper.buildFormLabel('Tìm kiếm')}
                <KTFormSearch
                  name="search"
                  placeholder="Theo mã phiên sạc, biển số xe, tên chủ xe, số điện thoại, dòng xe..."
                  value={filter?.q}
                  onSubmit={(text) => {
                    setFilter({
                      ...filter,
                      q: text,
                      page: 0,
                    });
                  }}
                />
              </div>
              {/* loc theo trang thai */}
              <div className="col-xl-3 col-lg-4 col-sm-6 mb-4 px-2">
                {UIHelper.buildFormLabel('Trạng thái')}
                <KTBSDropdown
                  name="status"
                  toggleElement={
                    <button
                      className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                      type="button"
                    >
                      {_.find(
                        ChargeHelper.Data.chargeTransactionStatusOptions,
                        (item) => item.value === filter?.status
                      )?.name ?? 'Tất cả'}
                    </button>
                  }
                  selectedValue={filter?.status}
                  dropdownMenuItems={[
                    { name: 'Tất cả', value: '' },
                    ...ChargeHelper.Data.chargeTransactionStatusOptions,
                  ]}
                  dropdownMenuClassName="overflow-auto w-100"
                  dropdownMenuStyle={{ maxHeight: '30vh' }}
                  onChange={(value) => {
                    setFilter({
                      ...filter,
                      status: value,
                      page: 0,
                    });
                  }}
                  isDropdownForm={true}
                />
              </div>
              {/* loc theo tram sac */}
              {!Utils.isValid(initialFilter?.pointId) && (
                <div className="col-xl-3 col-lg-4 col-sm-6 mb-4 px-2">
                  {UIHelper.buildFormLabel('Trạm sạc')}
                  {dropdownStationEl}
                </div>
              )}
              {/* loc theo khoang thoi gian */}
              <div className="col-xl-3 col-lg-4 col-sm-6 mb-4 px-2">
                {UIHelper.buildFormLabel('Khoảng thời gian')}
                <CustomDateRangePicker
                  toggleElementBorderRadius={5}
                  initialValue={filter?.dateFilter}
                  onPressSave={(selectedValue, startDate, endDate) => {
                    setFilter({
                      ...filter,
                      dateFilter: selectedValue,
                      from: startDate,
                      to: endDate,
                      page: 0,
                    });
                  }}
                />
              </div>

              {/* thiet lap cot */}
              {prefKey && (
                <div className="col-xl-3 col-lg-4 col-sm-6 mb-4 px-2 d-flex align-items-end gap-3">
                  <DropdownTableConfigColumn
                    columnIds={columnIds}
                    fullColumns={fullColumns}
                    fullColumnIds={fullColumnsIdSorted}
                    onChangeCheckboxValue={onChangeCheckboxValue}
                    resetDefaultTableColumn={resetDefaultTableColumn}
                    onChangeFullColumnIds={(ids) => {
                      setFullColumnIds(ids);
                      const key = prefKey + '_FULL_COLUMN_IDS';
                      localStorage.setItem(key, ids.join('|'));
                    }}
                    onSelectAll={onSelectAll}
                    onUnSelectAll={onUnSelectAll}
                    saveChanges={saveChanges}
                  />
                </div>
              )}
            </div>

            <div className="px-4">
              <KTTable
                columns={columns}
                items={chargeTransactionList}
                selectableRows={false}
                striped={false}
                compactGrid={false}
                onClickedRow={(row) => { }}
                onSelectedRowsChanged={(rows) => { }}
                onDoubleClickedRow={(row) => { }}
                noDataComponent={UIHelper.buildEmptyNoData(
                  'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                )}
              />
            </div>
          </div>

          {/* pagination */}
          <div className="px-4 mt-2">
            <KTPagination
              currentPage={filter?.page + 1}
              rowCount={total}
              rowsPerPage={filter?.limit}
              currentItemInPageCount={chargeTransactionList?.length}
              onChangePage={(newPage) => {
                const iNewPage = parseInt(newPage);
                setFilter({
                  ...filter,
                  page: iNewPage - 1,
                });
              }}
              onChangeRowsPerPage={(newRowsPerPage) => {
                const iNewRowsPerPage = parseInt(newRowsPerPage);
                setFilter({
                  ...filter,
                  limit: iNewRowsPerPage,
                  page: 0,
                });
              }}
              loading={false}
              color={KTPaginationColor.success}
              appendText="phiên sạc"
            />
          </div>
        </div>,
        false
      )}
    </div>
  );
}

export default ChargeTransactionCompactListView;
